import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FormRadioButton,
  Input,
  RadioButton,
  TextArea,
} from "../common/ControlBundle";
import BackIcon from "../common/BackIcon";
import { getFormsySubmissionsByFormId } from "../../scripts/formScripts";
import { openPDF } from "../../scripts/helperScripts";

function OHParentPartnerReport() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selfEntrySubmissions, setSelfEntrySubmissions] = useState([]);
  const [selfExitSubmissions, setSelfExitSubmissions] = useState([]);
  const [outcomeSubmissions, setOutcomeSubmissions] = useState([]);
  const isMountedRef = useRef(true);
  const pdfRef = useRef(true);

  useEffect(() => {
    return () => {
      pdfRef.current = false;
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!location.state.contact) {
      return;
    }

    const loadFormSubmissions = async () => {
      setLoading(true);
      const contactId = location.state.contact._id;
      const response = await Promise.all([
        getFormsySubmissionsByFormId(
          "2D1B55AD-1623-ED11-AE83-DC98408D5418", // Self-entry
          contactId
        ),
        getFormsySubmissionsByFormId(
          "2E1B55AD-1623-ED11-AE83-DC98408D5418", // Self-exit
          contactId
        ),
        getFormsySubmissionsByFormId(
          "B46396BF-1623-ED11-AE83-DC98408D5418", // Outcome
          contactId
        ),
      ]);

      if (!isMountedRef.current) {
        return;
      }

      setSelfEntrySubmissions(
        response[0]?.filter(
          (submission) =>
            submission.FinalSigned === "1" && submission.Active === "1"
        ) || []
      );
      setSelfExitSubmissions(
        response[1]?.filter(
          (submission) =>
            submission.FinalSigned === "1" && submission.Active === "1"
        ) || []
      );
      setOutcomeSubmissions(
        response[2]?.filter(
          (submission) =>
            submission.FinalSigned === "1" && submission.Active === "1"
        ) || []
      );
      setLoading(false);
    };

    loadFormSubmissions();
  }, [location.state.contact]);

  const sendToPDF = useCallback(async () => {
    if (!pdfRef.current) {
      return;
    }

    const toastId = toast.loading("Generating PDF...", {
      type: toast.TYPE.INFO,
    });

    try {
      await openPDF(pdfRef.current, "Parent Partner Assessment Report");

      toast.update(toastId, {
        render: "PDF successfully generated.",
        type: toast.TYPE.SUCCESS,
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });
    } catch {
      toast.update(toastId, {
        render: "An error occurred while generating the PDF.",
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });
    }
  }, [pdfRef]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
        <i
          tile="Create PDF"
          role="button"
          className="fas fa-file-pdf fa-2x text-primary ms-2"
          onClick={sendToPDF}
        ></i>
      </div>
      <div ref={pdfRef}>
        Client: {location.state.contact?.name}
        <br />
        {loading ? (
          <>Loading...</>
        ) : (
          <>
            <div className="h5">Self-Entry Assessments</div>
            {selfEntrySubmissions.length === 0
              ? "No Final Signed Self-Entry Assessments found"
              : selfEntrySubmissions.map((submission) => {
                  const data = submission.FormResults.results;

                  return (
                    <div className="mt-4">
                      {data && Object.keys(data).length > 0 && (
                        <>
                          <Input
                            name="Date"
                            label="Date:"
                            value={data.Date["@value"]}
                            disabled={true}
                            type="date"
                          />
                          <div className="container border">
                            <div className="row border-bottom">
                              <div className="col py-2 text-center">
                                Using the scale below, mark the number in the
                                far-right column to indicate the participant's
                                assessment on each item.
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col py-2 text-center border-end">
                                Never (1)
                              </div>
                              <div className="col py-2 text-center border-end">
                                Rarely (2)
                              </div>
                              <div className="col py-2 text-center border-end">
                                Sometimes (3)
                              </div>
                              <div className="col py-2 text-center border-end">
                                Often (4)
                              </div>
                              <div className="col py-2 text-center">
                                Always (5)
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">1.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I am able to find the community resources I need
                                to keep my child(ren) safe.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="FindResources"
                                  value={data.FindResources["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">2.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I am able to complete the steps necessary to get
                                the community resources I need.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="GetResources"
                                  value={data.GetResources["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">3.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I am able to effectively manage my situation to
                                keep my child(ren) safe when times are
                                stressful.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Safe"
                                  value={data.Safe["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">4.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I am able to make the appropriate decisions for
                                myself and my family.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Decisions"
                                  value={data.Decisions["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">5.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I have others who will listen when I need to
                                talk about my problems.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Listen"
                                  value={data.Listen["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">6.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I have others who will support positive choices
                                and changes I make.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Support"
                                  value={data.Support["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">7.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I talk reasonably and honestly with others about
                                my situation and problems.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Communication"
                                  value={data.Communication["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">8.</span>
                              </div>
                              <div className="col py-3 border-end">
                                If there is a crisis in my life I have someone I
                                can talk to.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Crisis"
                                  value={data.Crisis["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">9.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I am able to effectively speak up for myself and
                                my family to DHS and other service providers.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Advocate"
                                  value={data.Advocate["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">10.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I am able to listen to DHS and other service
                                providers and understand their concerns with my
                                situation.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Understand"
                                  value={data.Understand["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col-1 py-3 border-end">
                                <span className="float-end">11.</span>
                              </div>
                              <div className="col py-3 border-end">
                                I feel comfortable when talking with my DHS
                                worker or other service providers.
                              </div>
                              <div className="col-1 py-2">
                                <Input
                                  name="Comfortable"
                                  value={data.Comfortable["@value"]}
                                  disabled={true}
                                  style={{ marginBottom: "0px" }}
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col py-2">
                                <br />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col py-2">
                                <b>
                                  Using the scale below, please mark the box
                                  that best describes your current relationship
                                  with your DHS worker.
                                </b>
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col pt-3 border-end">
                                <FormRadioButton
                                  name="EntryRelationship"
                                  value={data.Relationship["@value"]}
                                  options={[
                                    {
                                      _id: "Very Negative",
                                      name: "Very Negative",
                                    },
                                  ]}
                                  disabled={true}
                                  optionsMarginClassName="me-2"
                                />
                              </div>
                              <div className="col pt-3 border-end">
                                <FormRadioButton
                                  name="EntryRelationship"
                                  value={data.Relationship["@value"]}
                                  options={[
                                    { _id: "Negative", name: "Negative" },
                                  ]}
                                  disabled={true}
                                  optionsMarginClassName="me-2"
                                />
                              </div>
                              <div className="col pt-3 border-end">
                                <FormRadioButton
                                  name="EntryRelationship"
                                  value={data.Relationship["@value"]}
                                  options={[
                                    { _id: "Neutral", name: "Neutral" },
                                  ]}
                                  disabled={true}
                                  optionsMarginClassName="me-2"
                                />
                              </div>
                              <div className="col pt-3 border-end">
                                <FormRadioButton
                                  name="EntryRelationship"
                                  value={data.Relationship["@value"]}
                                  options={[
                                    { _id: "Positive", name: "Positive" },
                                  ]}
                                  disabled={true}
                                  optionsMarginClassName="me-2"
                                />
                              </div>
                              <div className="col pt-3">
                                <FormRadioButton
                                  name="EntryRelationship"
                                  value={data.Relationship["@value"]}
                                  options={[
                                    {
                                      _id: "Very Positive",
                                      name: "Very Positive",
                                    },
                                  ]}
                                  disabled={true}
                                  optionsMarginClassName="me-2"
                                />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col py-2">
                                <br />
                              </div>
                            </div>
                            <div className="row border-bottom">
                              <div className="col py-2">
                                Please use the space below to provide any
                                additional comments regarding any of the
                                statements above.
                              </div>
                            </div>
                            <div className="row">
                              <div className="col py-2">
                                <TextArea
                                  name="Comments"
                                  value={data.Comments["@value"]}
                                  disabled={true}
                                  rows="5"
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

            <div className="h5 mt-5">Self-Exit Assessments</div>
            {selfExitSubmissions.length === 0
              ? "No Final Signed Self-Exit Assessments found"
              : selfExitSubmissions.map((submission) => {
                  const data = submission.FormResults.results;

                  return (
                    <div>
                      <Input
                        name="Date"
                        label="Date:"
                        value={data.Date["@value"]}
                        disabled={true}
                        type="date"
                      />
                      <div className="container border">
                        <div className="row border-bottom">
                          <div className="col py-2 text-center">
                            Using the scale below, mark the number in the
                            correct column to indicate the participant's
                            assessment on each item.
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 text-center border-end">
                            Never (1)
                          </div>
                          <div className="col py-2 text-center border-end">
                            Rarely (2)
                          </div>
                          <div className="col py-2 text-center border-end">
                            Sometimes (3)
                          </div>
                          <div className="col py-2 text-center border-end">
                            Often (4)
                          </div>
                          <div className="col py-2 text-center">Always (5)</div>
                        </div>
                        <div className="row border-bottom ">
                          <div className="col py-2 ">
                            <b>
                              RETROSPECTIVE
                              <br />
                              ASSESSMENT
                            </b>
                          </div>
                          <div className="col"></div>
                          <div className="col py-2 text-end">
                            <b>
                              EXIT
                              <br />
                              ASSESSMENT
                            </b>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="FindResourcesEntry"
                              value={data.FindResourcesEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">1.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I am able to find the community resources I need to
                            keep my child(ren) safe.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="FindResourcesExit"
                              value={data.FindResourcesExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="GetResourcesEntry"
                              value={data.GetResourcesEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">2.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I am able to complete the steps necessary to get the
                            community resources I need.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="GetResourcesExit"
                              value={data.GetResourcesExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="SafeEntry"
                              value={data.SafeEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">3.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I am able to effectively manage my situation to keep
                            my child(ren) safe when times are stressful.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="SafeExit"
                              value={data.SafeExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="DecisionsEntry"
                              value={data.DecisionsEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">4.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I am able to make the appropriate decisions for
                            myself and my family.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="DecisionsExit"
                              value={data.DecisionsExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="ListenEntry"
                              value={data.ListenEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">5.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I have others who will listen when I need to talk
                            about my problems.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="ListenExit"
                              value={data.ListenExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="SupportEntry"
                              value={data.SupportEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">6.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I have others who will support positive choices and
                            changes I make.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="SupportExit"
                              value={data.SupportExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="CommunicationEntry"
                              value={data.CommunicationEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">7.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I talk reasonably and honestly with others about my
                            situation and problems.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="CommunicationExit"
                              value={data.CommunicationExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="CrisisEntry"
                              value={data.CrisisEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">8.</span>
                          </div>
                          <div className="col py-3 border-end">
                            If there is a crisis in my life I have someone I can
                            talk to.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="CrisisExit"
                              value={data.CrisisExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="AdvocateEntry"
                              value={data.AdvocateEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">9.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I am able to effectively speak up for myself and my
                            family to DHS and other service providers.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="AdvocateExit"
                              value={data.AdvocateExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="UnderstandEntry"
                              value={data.UnderstandEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">10.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I am able to listen to DHS and other service
                            providers and understand their concerns with my
                            situation.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="UnderstandExit"
                              value={data.UnderstandExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col-1 py-2 border-end">
                            <Input
                              name="ComfortableEntry"
                              value={data.ComfortableEntry["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                          <div className="col-1 py-3 border-end">
                            <span className="float-end">11.</span>
                          </div>
                          <div className="col py-3 border-end">
                            I feel comfortable when talking with my DHS worker
                            or other service providers.
                          </div>
                          <div className="col-1 py-2">
                            <Input
                              name="ComfortableExit"
                              value={data.ComfortableExit["@value"]}
                              disabled={true}
                              style={{ marginBottom: "0px" }}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2">
                            <br />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2">
                            <b>
                              Using the scale below, please mark the box that
                              best describes your current relationship with your
                              DHS worker.
                            </b>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col pt-3 border-end">
                            <FormRadioButton
                              name="ExitRelationship"
                              value={data.Relationship["@value"]}
                              options={[
                                { _id: "Very Negative", name: "Very Negative" },
                              ]}
                              disabled={true}
                              optionsMarginClassName="me-2"
                            />
                          </div>
                          <div className="col pt-3 border-end">
                            <FormRadioButton
                              name="ExitRelationship"
                              value={data.Relationship["@value"]}
                              options={[{ _id: "Negative", name: "Negative" }]}
                              disabled={true}
                              optionsMarginClassName="me-2"
                            />
                          </div>
                          <div className="col pt-3 border-end">
                            <FormRadioButton
                              name="ExitRelationship"
                              value={data.Relationship["@value"]}
                              options={[{ _id: "Neutral", name: "Neutral" }]}
                              disabled={true}
                              optionsMarginClassName="me-2"
                            />
                          </div>
                          <div className="col pt-3 border-end">
                            <FormRadioButton
                              name="ExitRelationship"
                              value={data.Relationship["@value"]}
                              options={[{ _id: "Positive", name: "Positive" }]}
                              disabled={true}
                              optionsMarginClassName="me-2"
                            />
                          </div>
                          <div className="col pt-3">
                            <FormRadioButton
                              name="ExitRelationship"
                              value={data.Relationship["@value"]}
                              options={[
                                { _id: "Very Positive", name: "Very Positive" },
                              ]}
                              disabled={true}
                              optionsMarginClassName="me-2"
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2">
                            <br />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2">
                            Please use the space below to provide any additional
                            comments regarding any of the statements above.
                          </div>
                        </div>
                        <div className="row">
                          <div className="col py-2">
                            <TextArea
                              name="Comments"
                              value={data.Comments["@value"]}
                              disabled={true}
                              rows="5"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

            <div className="h5 mt-5">Outcome Assessments</div>
            {outcomeSubmissions.length === 0
              ? "No Final Signed Outcome Assessments found"
              : outcomeSubmissions.map((submission) => {
                  const data = submission.FormResults.results;

                  return (
                    <div>
                      <div className="row">
                        <div className="col">
                          <Input
                            name="Parent"
                            label="Parent Partner:"
                            value={data.Parent["@value"]}
                            disabled={true}
                          />
                        </div>
                        <div className="col">
                          <Input
                            name="Date"
                            label="Date:"
                            value={data.Date["@value"]}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <br />
                      <div className="container border">
                        <div className="row border-bottom">
                          <div className="col py-2">
                            Using the scale below, mark the appropriate box to
                            indicate your response for each item.
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 text-center border-end">
                            Never
                          </div>
                          <div className="col py-2 text-center border-end">
                            Rarely
                          </div>
                          <div className="col py-2 text-center border-end">
                            Sometimes
                          </div>
                          <div className="col py-2 text-center border-end">
                            Often
                          </div>
                          <div className="col py-2 text-center border-end">
                            Always
                          </div>
                          <div className="col py-2 text-center">
                            My parent declined or did not participate
                            <br />
                            (N/A)
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2">The Parent Partner...</div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>1.</div>
                            <div className="flex-fill">
                              Encouraged the participant to fulfill their case
                              plan activities
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Fulfill"
                              value={data.Fulfill["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Fulfill"
                              value={data.Fulfill["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Fulfill"
                              value={data.Fulfill["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Fulfill"
                              value={data.Fulfill["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Fulfill"
                              value={data.Fulfill["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Fulfill"
                              value={data.Fulfill["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>2.</div>
                            <div className="flex-fill">
                              Had regular face to face visits with the
                              participant
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Visits"
                              value={data.Visits["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Visits"
                              value={data.Visits["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Visits"
                              value={data.Visits["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Visits"
                              value={data.Visits["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Visits"
                              value={data.Visits["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Visits"
                              value={data.Visits["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>3.</div>
                            <div className="flex-fill">
                              Had other (email, phone, web) communication and
                              contact with the participant
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="OtherContact"
                              value={data.OtherContact["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="OtherContact"
                              value={data.OtherContact["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="OtherContact"
                              value={data.OtherContact["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="OtherContact"
                              value={data.OtherContact["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="OtherContact"
                              value={data.OtherContact["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="OtherContact"
                              value={data.OtherContact["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>4.</div>
                            <div className="flex-fill">
                              Advocated for the participant for needed resources
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocated"
                              value={data.Advocated["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocated"
                              value={data.Advocated["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocated"
                              value={data.Advocated["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocated"
                              value={data.Advocated["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocated"
                              value={data.Advocated["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Advocated"
                              value={data.Advocated["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>5.</div>
                            <div className="flex-fill">
                              Encouraged the participant
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Encouraged"
                              value={data.Encouraged["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Encouraged"
                              value={data.Encouraged["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Encouraged"
                              value={data.Encouraged["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Encouraged"
                              value={data.Encouraged["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Encouraged"
                              value={data.Encouraged["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Encouraged"
                              value={data.Encouraged["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>6.</div>
                            <div className="flex-fill">
                              Connected the participant with community resources
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Connected"
                              value={data.Connected["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Connected"
                              value={data.Connected["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Connected"
                              value={data.Connected["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Connected"
                              value={data.Connected["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Connected"
                              value={data.Connected["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Connected"
                              value={data.Connected["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>7.</div>
                            <div className="flex-fill">
                              Helped the participant connect with the community
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="HelpConnect"
                              value={data.HelpConnect["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="HelpConnect"
                              value={data.HelpConnect["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="HelpConnect"
                              value={data.HelpConnect["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="HelpConnect"
                              value={data.HelpConnect["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="HelpConnect"
                              value={data.HelpConnect["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="HelpConnect"
                              value={data.HelpConnect["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>8.</div>
                            <div className="flex-fill">
                              Coached the participant on communication
                              strategies
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Communication"
                              value={data.Communication["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Communication"
                              value={data.Communication["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Communication"
                              value={data.Communication["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Communication"
                              value={data.Communication["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Communication"
                              value={data.Communication["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Communication"
                              value={data.Communication["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>9.</div>
                            <div className="flex-fill">
                              Supported the participant at FTDM, court,
                              treatment, other gatherings
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Supported"
                              value={data.Supported["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Supported"
                              value={data.Supported["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Supported"
                              value={data.Supported["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Supported"
                              value={data.Supported["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Supported"
                              value={data.Supported["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Supported"
                              value={data.Supported["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>10.</div>
                            <div className="flex-fill">
                              Coached the participant on what to expect
                              throughout the process
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Coached"
                              value={data.Coached["@value"]}
                              options={[{ _id: "Never", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Coached"
                              value={data.Coached["@value"]}
                              options={[{ _id: "Rarely", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Coached"
                              value={data.Coached["@value"]}
                              options={[{ _id: "Sometimes", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Coached"
                              value={data.Coached["@value"]}
                              options={[{ _id: "Often", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Coached"
                              value={data.Coached["@value"]}
                              options={[{ _id: "Always", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Coached"
                              value={data.Coached["@value"]}
                              options={[{ _id: "N/A", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2">
                            Please provide additional comments regarding the
                            performance on these activities throughout the
                            Parent Partner Program. If you rated any of the
                            above statements as "Always" or "Rarely" or "Never"
                            please explain why below.
                          </div>
                        </div>
                        <div className="row">
                          <div className="col py-2">
                            <TextArea
                              name="Comments"
                              value={data.Comments["@value"]}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                      <br />
                      <div className="container border">
                        <div className="row border-bottom">
                          <div className="col py-2">
                            Using the scale below, mark the appropriate box to
                            indicate your response for each item.
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 text-center border-end">
                            Significant Improvement
                          </div>
                          <div className="col py-2 text-center border-end">
                            Some Improvement
                          </div>
                          <div className="col py-2 text-center border-end">
                            Remained the Same
                          </div>
                          <div className="col py-2 text-center border-end">
                            Decreased
                          </div>
                          <div className="col py-2 text-center border-end">
                            Don't know
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2">
                            Please rate the participant improvement on their...
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>1.</div>
                            <div className="flex-fill">
                              Relationship with people who are able to connect
                              them with resources
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="ConnectRelationship"
                              value={data.ConnectRelationship["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="ConnectRelationship"
                              value={data.ConnectRelationship["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="ConnectRelationship"
                              value={data.ConnectRelationship["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="ConnectRelationship"
                              value={data.ConnectRelationship["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="ConnectRelationship"
                              value={data.ConnectRelationship["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>2.</div>
                            <div className="flex-fill">
                              Relationship with people who support their
                              positive changes
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="SupportRelationship"
                              value={data.SupportRelationship["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="SupportRelationship"
                              value={data.SupportRelationship["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="SupportRelationship"
                              value={data.SupportRelationship["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="SupportRelationship"
                              value={data.SupportRelationship["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="SupportRelationship"
                              value={data.SupportRelationship["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>3.</div>
                            <div className="flex-fill">
                              Level of communication with their DHS worker
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="DHSCommunication"
                              value={data.DHSCommunication["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="DHSCommunication"
                              value={data.DHSCommunication["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="DHSCommunication"
                              value={data.DHSCommunication["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="DHSCommunication"
                              value={data.DHSCommunication["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="DHSCommunication"
                              value={data.DHSCommunication["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>4.</div>
                            <div className="flex-fill">
                              Level of communication with attorney(s)
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="AttorneyCommunication"
                              value={data.AttorneyCommunication["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="AttorneyCommunication"
                              value={data.AttorneyCommunication["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="AttorneyCommunication"
                              value={data.AttorneyCommunication["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="AttorneyCommunication"
                              value={data.AttorneyCommunication["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="AttorneyCommunication"
                              value={data.AttorneyCommunication["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>5.</div>
                            <div className="flex-fill">
                              Ability to advocate appropriately for themselves
                              &amp; family
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocate"
                              value={data.Advocate["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocate"
                              value={data.Advocate["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocate"
                              value={data.Advocate["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Advocate"
                              value={data.Advocate["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Advocate"
                              value={data.Advocate["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>6.</div>
                            <div className="flex-fill">
                              Knowledge of what needs to be done for custody of
                              their children
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Custody"
                              value={data.Custody["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Custody"
                              value={data.Custody["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Custody"
                              value={data.Custody["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Custody"
                              value={data.Custody["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Custody"
                              value={data.Custody["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>7.</div>
                            <div className="flex-fill">
                              Ability to get to appointments on time
                              (visitation, FTMs, counseling session, substance
                              abuse treatment, etc.)
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="ReliableAppts"
                              value={data.ReliableAppts["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="ReliableAppts"
                              value={data.ReliableAppts["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="ReliableAppts"
                              value={data.ReliableAppts["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="ReliableAppts"
                              value={data.ReliableAppts["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="ReliableAppts"
                              value={data.ReliableAppts["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>8.</div>
                            <div className="flex-fill">
                              Ability to find community resources for their
                              family
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="FindResources"
                              value={data.FindResources["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="FindResources"
                              value={data.FindResources["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="FindResources"
                              value={data.FindResources["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="FindResources"
                              value={data.FindResources["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="FindResources"
                              value={data.FindResources["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>9.</div>
                            <div className="flex-fill">
                              Knowledge of who to contact with needs or concerns
                              regarding their case
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="NeedsContact"
                              value={data.NeedsContact["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="NeedsContact"
                              value={data.NeedsContact["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="NeedsContact"
                              value={data.NeedsContact["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="NeedsContact"
                              value={data.NeedsContact["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="NeedsContact"
                              value={data.NeedsContact["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>10.</div>
                            <div className="flex-fill">
                              Level of personal responsibility and
                              accountability for their actions
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Responsibility"
                              value={data.Responsibility["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Responsibility"
                              value={data.Responsibility["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Responsibility"
                              value={data.Responsibility["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Responsibility"
                              value={data.Responsibility["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Responsibility"
                              value={data.Responsibility["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom py-1">
                          <div className="col d-flex">
                            <div style={{ width: "32px" }}>11.</div>
                            <div className="flex-fill">
                              Willingness to make changes
                            </div>
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Willingness"
                              value={data.Willingness["@value"]}
                              options={[
                                { _id: "Significant Improvement", name: "" },
                              ]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Willingness"
                              value={data.Willingness["@value"]}
                              options={[{ _id: "Some Improvement", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Willingness"
                              value={data.Willingness["@value"]}
                              options={[{ _id: "Remained the Same", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2 border-end">
                            <RadioButton
                              name="Willingness"
                              value={data.Willingness["@value"]}
                              options={[{ _id: "Decreased", name: "" }]}
                              disabled={true}
                            />
                          </div>
                          <div className="col py-2">
                            <RadioButton
                              name="Willingness"
                              value={data.Willingness["@value"]}
                              options={[{ _id: "Don't Know", name: "" }]}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom">
                          <div className="col py-2">
                            If you rated any of the above statemens as
                            "Significant Improvement" or "Remaind the Same" or
                            "Decreased," please explain why below. Note that
                            "Remained the Same" could mean that no change was
                            needed, or the indicator was satisfactory to begin
                            with.
                          </div>
                        </div>
                        <div className="row">
                          <div className="col py-2">
                            <TextArea
                              name="RatingComments"
                              value={data.RatingComments["@value"]}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
          </>
        )}
      </div>
    </>
  );
}

export default OHParentPartnerReport;
